import React from 'react';
import type { NumericFormatProps } from 'react-number-format';
import { NumericFormat } from 'react-number-format';
import { omit } from 'lodash';

import type { InputLabelProps } from '~/components/core/Atomic/InputLabel/InputLabel';
import onUserValueChange from '~/components/core/Molecules/Fields/onUserValueChange';
import type { TextFieldProps } from '~/components/core/Molecules/Fields/TextField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import { isLocalEnv, reportErrorInProductionOrThrow } from '~/Utils';

type CustomInputProps = TextFieldProps<string>;

// The numeric-format props plus whatever the custom input accepts; `customInput` is exposed as
// `CustomInput`, and `value`/`onChange` are re-declared with the numeric contract callers rely on.
export interface NumericTextFieldProps
  extends Omit<NumericFormatProps<CustomInputProps>, 'customInput' | 'onValueChange' | 'value' | 'onChange'> {
  value?: number | string;
  symbol?: string;
  groupSeparator?: string;
  decimalSeparator?: string;
  onChange?: (value: number | undefined) => void;
  CustomInput?: React.ComponentType<CustomInputProps>;
}

export interface MonetaryValueTextFieldProps {
  id?: string;
  className?: string;
  label?: string;
  showOnly?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  onChange?: (value: number | undefined) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  InputLabelProps?: Partial<InputLabelProps>;
  value?: number | string;
  inlineEditableField?: boolean;
}

export const NumericTextField: React.FC<NumericTextFieldProps> = ({
  id,
  label,
  value,
  groupSeparator,
  decimalSeparator,
  placeholder,
  symbol,
  onChange,
  CustomInput,
  inlineEditableField,
  className,
  onFocus,
  onBlur,
  ...rest
}) => {
  return (
    <NumericFormat
      customInput={CustomInput}
      value={value}
      thousandSeparator={groupSeparator ? groupSeparator : true}
      decimalSeparator={decimalSeparator || '*'} // decimal separator must have 1 character value
      decimalScale={!decimalSeparator ? 0 : 2} // if currency does not have decimalSeparator set decimalScale to 0
      id={id}
      label={label}
      prefix={symbol}
      placeholder={placeholder}
      onValueChange={onUserValueChange((numberValues) => {
        const { floatValue } = numberValues;
        if (onChange) {
          onChange(floatValue);
        }
      })}
      onFocus={onFocus}
      onBlur={onBlur}
      inlineEditableField={inlineEditableField}
      className={className}
      {...rest}
    />
  );
};

const CustomInput = ({ inlineEditableField, className, ...customInputProps }: TextFieldProps<string>) => (
  <TextField
    id="text-field"
    {...omit(customInputProps, ['inputMode'])}
    preventOverrideOnChange
    inlineEditableField={inlineEditableField}
    className={className}
  />
);

export const MonetaryValueTextField: React.FC<MonetaryValueTextFieldProps> = ({
  id,
  label,
  fullWidth,
  disabled,
  value,
  onChange,
  inlineEditableField,
  className,
  onFocus,
  onBlur,
  ...rest
}) => {
  const { currencyFormatter, symbol, groupSeparator, decimalSeparator } = useCurrencyFormatter();

  if (Object.keys(rest).length > 0 && isLocalEnv()) {
    reportErrorInProductionOrThrow(
      `Additional unimplemented props was passed to MonetaryValueTextField component (directly or from Multi/Single select fields), ${Object.keys(
        rest
      )}`,
      true
    );
  }

  return (
    <NumericTextField
      id={id}
      label={label}
      thousandSeparator={groupSeparator ? groupSeparator : true}
      decimalSeparator={decimalSeparator || '*'} // decimal separator must have 1 character value
      decimalScale={!decimalSeparator ? 0 : 2} // if currency does not have decimalSeparator set decimalScale to 0
      fixedDecimalScale
      allowNegative
      prefix={symbol}
      fullWidth={fullWidth}
      placeholder={currencyFormatter.format(0)}
      disabled={disabled}
      onChange={onChange}
      value={value}
      CustomInput={CustomInput}
      inlineEditableField={inlineEditableField}
      className={className}
      onFocus={onFocus}
      onBlur={onBlur}
      {...rest}
    />
  );
};
